import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useAuth } from '../../contexts/AuthContext';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import UserDeleteModal from '../../components/UserDeleteModal';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';
import {
  PencilIcon,
  EyeIcon,
  LockClosedIcon,
  LockOpenIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  displayName: string | null;
  email: string;
  role: {
    id: string;
    name: string;
    roleType: string;
    level: number;
  };
  roles?: string[];
  status: string;
  title: string | null;
  department: { id: string; name: string } | null;
  active: boolean;
  lockedOut: boolean;
  businessPhone: string | null;
  mobilePhone: string | null;
  timezone: string;
  region: string;
  createdAt: string;
  lastLogin: string | null;
  managerId?: string | null;
  Manager?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

interface EditingField {
  userId: string;
  field: string;
  value: any;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const timeRangeOptions = [
  { value: 'preset:today', label: 'Today' },
  { value: 'preset:yesterday', label: 'Yesterday' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
];

const convertPresetToCustom = (timeRangeValue: string): string => {
  if (!timeRangeValue.startsWith('preset:')) return timeRangeValue;
  const preset = timeRangeValue.replace('preset:', '') as DurationPreset;
  const range = getDurationPresetRange(preset);
  if (range.from && range.to) return `custom:${range.from},${range.to}`;
  return timeRangeValue;
};

function parseOptionalTimeFilter(sp: URLSearchParams, key: string): string {
  const t = sp.get(key);
  if (!t || t === 'all') return '';
  return t;
}

function appendDateFilterParams(params: URLSearchParams, prefix: string, rangeValue: string): void {
  if (!rangeValue) return;
  const converted = convertPresetToCustom(rangeValue);
  if (converted.startsWith('custom:')) {
    const [, dates] = converted.split('custom:');
    const [from, to] = dates.split(',');
    if (from) params.append(`${prefix}From`, from);
    if (to) params.append(`${prefix}To`, to);
  } else {
    params.append(`${prefix}Range`, converted);
  }
}

function formatTimeFilterChipLabel(rangeValue: string): string {
  if (!rangeValue) return '';
  if (rangeValue.startsWith('preset:')) {
    return timeRangeOptions.find((t) => t.value === rangeValue)?.label || rangeValue;
  }
  const option = timeRangeOptions.find((t) => t.value === rangeValue);
  if (option) return option.label;
  if (rangeValue.startsWith('custom:')) {
    const [, dates] = rangeValue.split('custom:');
    const [from, to] = dates.split(',');
    if (from && to) {
      try {
        return `${new Date(from.trim()).toLocaleString()} - ${new Date(to.trim()).toLocaleString()}`;
      } catch {
        return rangeValue;
      }
    }
  }
  return rangeValue;
}

const UserList = () => {
  const { formatDate, formatTime } = useDateFormatter();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/users', LIST_FILTER_SESSION_KEYS.users);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const appliedSearchTerm = searchParams.get('search') ?? '';
  const filterRole = searchParams.get('roleId') ?? '';
  const filterTitle = searchParams.get('title') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterEmail = searchParams.get('email') ?? '';
  const filterManager = searchParams.get('manager') ?? '';
  const filterActive = searchParams.get('active') ?? '';
  const filterLastLogin = useMemo(
    () => parseOptionalTimeFilter(searchParams, 'lastLogin'),
    [searchParams]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);
  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showCreateButton, showEditButton, canDeleteEntity } = useUserPermissions();

  // Check if current user can create/edit/delete users based on Contact user restrictions
  const canCreateUsers = showCreateButton('users');
  const canEditUsers = showEditButton('users');
  const canDeleteUsers = canDeleteEntity('users');

  // Fetch available roles for the dropdown
  const { data: rolesData } = useQuery({
    queryKey: ['creatable-roles'],
    queryFn: async () => {
      const response = await api.get('/users/creatable-roles');
      return response.data;
    }
  });

  // Fetch unique titles for the dropdown
  const { data: titlesData } = useQuery({
    queryKey: ['user-titles'],
    queryFn: async () => {
      const response = await api.get('/users/titles');
      return response.data;
    }
  });

  const { data: usersDropdownData } = useQuery({
    queryKey: ['users-dropdown'],
    queryFn: async () => {
      const response = await api.get('/users/dropdown');
      return response.data;
    },
  });

  const roleOptions = useMemo(
    () =>
      rolesData?.roles?.map((role: { id: string; name: string }) => ({
        value: role.id,
        label: role.name,
      })) ?? [],
    [rolesData]
  );

  const titleOptions = useMemo(
    () =>
      titlesData?.titles?.map((title: string) => ({
        value: title,
        label: title,
      })) ?? [],
    [titlesData]
  );

  const managerOptions = useMemo(
    () =>
      usersDropdownData?.users?.map((u: { id: string; firstName?: string; lastName?: string; email?: string }) => ({
        value: u.id,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unknown',
      })) ?? [],
    [usersDropdownData]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, pageSize, appliedSearchTerm, filterRole, filterTitle, filterName, filterEmail, filterManager, filterActive, filterLastLogin],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
      });
      if (appliedSearchTerm) params.append('search', appliedSearchTerm);
      if (filterRole) params.append('roleId', filterRole);
      if (filterTitle) params.append('title', filterTitle);
      if (filterName) params.append('name', filterName);
      if (filterEmail) params.append('email', filterEmail);
      if (filterManager) params.append('managerId', filterManager);
      if (filterActive === 'true' || filterActive === 'false') {
        params.append('active', filterActive);
      } else if (filterActive === 'all') {
        params.append('includeInactive', 'true');
      }
      appendDateFilterParams(params, 'lastLogin', filterLastLogin);

      const response = await api.get(`/users?${params}`);
      return response.data;
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, field, value }: { userId: string; field: string; value: any }) => {
      const response = await api.patch(`/users/${userId}`, { [field]: value });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated successfully');
      setEditingField(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user');
      setEditingField(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async ({ userId, reassignTo }: { userId: string; reassignTo?: string }) => {
      const url = reassignTo
        ? `/users/${userId}?reassignTo=${reassignTo}`
        : `/users/${userId}`;
      const response = await api.delete(url);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted successfully');
      setDeleteModalOpen(false);
      setUserToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
      setDeleteModalOpen(false);
      setUserToDelete(null);
    },
  });

  const syncUsersMutation = useMutation({
    mutationFn: async () => {
      // This would typically fetch users from an external system
      // For demo purposes, we'll create some sample users
      const sampleUsers = [
        {
          email: 'john.doe@example.com',
          firstName: 'John',
          lastName: 'Doe',
          title: 'Software Engineer',
          role: 'user',
          status: 'active',
        },
        {
          email: 'jane.smith@example.com',
          firstName: 'Jane',
          lastName: 'Smith',
          title: 'Product Manager',
          role: 'agent',
          status: 'active',
        },
      ];
      
      const response = await api.post('/users/sync', { users: sampleUsers });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(
        `Sync completed: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`
      );
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to sync users');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchInput.trim() || null,
      page: '1',
    });
  };

  const handleInlineEdit = (userId: string, field: string, currentValue: any) => {
    setEditingField({ userId, field, value: currentValue });
  };

  const handleInlineEditSave = () => {
    if (editingField) {
      updateUserMutation.mutate({
        userId: editingField.userId,
        field: editingField.field,
        value: editingField.value,
      });
    }
  };

  const handleInlineEditCancel = () => {
    setEditingField(null);
  };

  const handleSyncUsers = () => {
    confirmAlert({
      title: 'Sync Users',
      message: 'This will sync users from the external system. Continue?',
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm'
        },
        {
          label: 'Sync',
          onClick: () => syncUsersMutation.mutate(),
          className: 'px-6 py-2.5 text-sm text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm'
        }
      ],
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
      customUI: ({ onClose, title, message }) => (
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 border border-gray-100">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
            </div>
          </div>
          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
            <button
              className="px-6 py-2.5 text-sm text-gray-700 bg-gray-200 border border-gray-300 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-6 py-2.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-all duration-200 font-medium"
              onClick={() => {
                onClose();
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )
    });
  };

  const handleDeleteUser = (userId: string, userName: string) => {
    console.log('handleDeleteUser called:', { userId, userName });

    // Additional frontend check to prevent self-deletion
    if (userId === user?.id) {
      toast.error('Cannot delete your own account');
      return;
    }

    console.log('Setting user to delete and opening modal');
    setUserToDelete({ id: userId, name: userName });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (reassignToUserId?: string) => {
    if (!userToDelete) return;

    await deleteUserMutation.mutateAsync({
      userId: userToDelete.id,
      reassignTo: reassignToUserId
    });
  };

  const renderEditableField = (user: User, field: string, value: any) => {
    const isEditing = editingField?.userId === user.id && editingField?.field === field;
    
    // Don't show edit controls for Contact users
    if (!canEditUsers) {
      if (field === 'active') {
        return (
          <span className={`px-2 py-1 rounded-full text-xs ${
            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {value ? 'Active' : 'Inactive'}
          </span>
        );
      }
      if (field === 'lockedOut') {
        return value ? (
          <LockClosedIcon className="h-4 w-4 text-red-600" />
        ) : (
          <LockOpenIcon className="h-4 w-4 text-green-600" />
        );
      }
      return <span className="text-sm">{value || '-'}</span>;
    }

    if (isEditing) {
      if (field === 'active' || field === 'lockedOut') {
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={editingField.value}
              onChange={(e) => setEditingField({ ...editingField, value: e.target.checked })}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <button
              onClick={handleInlineEditSave}
              className="text-green-600 hover:text-green-800"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              onClick={handleInlineEditCancel}
              className="text-red-600 hover:text-red-800"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        );
      }

      if (field === 'role' || field === 'status') {
        const options = field === 'role' 
          ? ['user', 'manager', 'admin']
          : ['active', 'inactive', 'suspended', 'pending'];
        
        return (
          <div className="flex items-center space-x-2">
            <select
              value={editingField.value}
              onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
              className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {options.map(option => (
                <option key={option} value={option}>
                  {option.replace('_', ' ')}
                </option>
              ))}
            </select>
            <button
              onClick={handleInlineEditSave}
              className="text-green-600 hover:text-green-800"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              onClick={handleInlineEditCancel}
              className="text-red-600 hover:text-red-800"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        );
      }


      return (
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={editingField.value || ''}
            onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
            className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleInlineEditSave}
            className="text-green-600 hover:text-green-800"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
          <button
            onClick={handleInlineEditCancel}
            className="text-red-600 hover:text-red-800"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      );
    }

    // Render non-editing state
    if (field === 'active') {
      return (
        <button
          onClick={() => handleInlineEdit(user.id, field, value)}
          className="flex items-center space-x-1 text-sm"
        >
          <span className={`px-2 py-1 rounded-full text-xs ${
            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {value ? 'Active' : 'Inactive'}
          </span>
          <PencilIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
        </button>
      );
    }

    if (field === 'lockedOut') {
      return (
        <button
          onClick={() => handleInlineEdit(user.id, field, value)}
          className="flex items-center space-x-1"
        >
          {value ? (
            <LockClosedIcon className="h-4 w-4 text-red-600" />
          ) : (
            <LockOpenIcon className="h-4 w-4 text-green-600" />
          )}
          <PencilIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
        </button>
      );
    }

    return (
      <button
        onClick={() => handleInlineEdit(user.id, field, value)}
        className="flex items-center space-x-1 text-sm text-left hover:bg-gray-50 rounded px-1"
      >
        <span>{value || '-'}</span>
        <PencilIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
      </button>
    );
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'email':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterEmail}
              placeholder="Filter by email..."
              onChange={(v) => {
                patchSearchParams({ email: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'role':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={roleOptions}
              value={filterRole}
              placeholder="All Roles"
              onChange={(v) => {
                patchSearchParams({ roleId: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'title':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={titleOptions}
              value={filterTitle}
              placeholder="All Titles"
              onChange={(v) => {
                patchSearchParams({ title: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'manager':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={managerOptions}
              value={filterManager}
              placeholder="All Managers"
              onChange={(v) => {
                patchSearchParams({ manager: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
                { value: 'all', label: 'All' },
              ]}
              value={filterActive || 'true'}
              placeholder="Active"
              active={searchParams.has('active')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                if (!val || val === 'true') {
                  patchSearchParams({ active: null, page: '1' });
                } else {
                  patchSearchParams({ active: val, page: '1' });
                }
              }}
            />
          );
        case 'lastLogin':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={timeRangeOptions}
              value={filterLastLogin || 'all'}
              placeholder="All Time"
              active={searchParams.has('lastLogin')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                patchSearchParams({ lastLogin: val && val !== 'all' ? val : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterName,
      filterEmail,
      roleOptions,
      filterRole,
      titleOptions,
      filterTitle,
      managerOptions,
      filterManager,
      filterActive,
      filterLastLogin,
      searchParams,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearchTerm) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearchTerm}"`, color: 'blue' });
    }
    if (filterName) chips.push({ id: 'name', label: 'Name', value: filterName, color: 'blue' });
    if (filterEmail) chips.push({ id: 'email', label: 'Email', value: filterEmail, color: 'blue' });
    if (filterRole) {
      const role = roleOptions.find((r: { value: string; label: string }) => r.value === filterRole);
      chips.push({ id: 'roleId', label: 'Role', value: role?.label || 'Unknown', color: 'purple' });
    }
    if (filterTitle) chips.push({ id: 'title', label: 'Title', value: filterTitle, color: 'green' });
    if (filterManager) {
      const manager = managerOptions.find((m: { value: string; label: string }) => m.value === filterManager);
      chips.push({ id: 'manager', label: 'Manager', value: manager?.label || 'Unknown', color: 'purple' });
    }
    if (filterActive === 'false') {
      chips.push({ id: 'active', label: 'Status', value: 'Inactive', color: 'gray' });
    } else if (filterActive === 'all') {
      chips.push({ id: 'active', label: 'Status', value: 'All', color: 'gray' });
    }
    if (filterLastLogin) {
      chips.push({ id: 'lastLogin', label: 'Last Login', value: formatTimeFilterChipLabel(filterLastLogin), color: 'orange' });
    }
    return chips;
  }, [appliedSearchTerm, filterName, filterEmail, filterRole, filterTitle, filterManager, filterActive, filterLastLogin, roleOptions, managerOptions]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'email':
        patchSearchParams({ email: null, page: '1' });
        break;
      case 'roleId':
        patchSearchParams({ roleId: null, page: '1' });
        break;
      case 'title':
        patchSearchParams({ title: null, page: '1' });
        break;
      case 'manager':
        patchSearchParams({ manager: null, page: '1' });
        break;
      case 'active':
        patchSearchParams({ active: null, page: '1' });
        break;
      case 'lastLogin':
        patchSearchParams({ lastLogin: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      email: null,
      roleId: null,
      title: null,
      manager: null,
      active: null,
      lastLogin: null,
      page: '1',
    });
  };

  if (isLoading) return <div className="p-4">Loading users...</div>;
  if (error) return <div className="p-4 text-red-600">Error loading users</div>;

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Title */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Users</h1>
            </div>
            {/* Right: Search and Buttons */}
            <div className="flex items-center gap-2">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative" style={{ width: '300px' }}>
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search users..."
                    className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={searchInput.length > 0 && searchInput.length < 3}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
                >
                  Search
                </button>
              </form>
              {canCreateUsers && (
                <button
                  onClick={() =>
                    navigate('/users/new', {
                      state: { usersListSearch: location.search },
                    })
                  }
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Create User
                </button>
              )}
              <button
                onClick={handleSyncUsers}
                disabled={syncUsersMutation.isPending}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`-ml-1 mr-2 h-5 w-5 ${syncUsersMutation.isPending ? 'animate-spin' : ''}`} />
                Sync Users
              </button>
            </div>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('name', 'Name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('email', 'Email')}
              </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('role', 'Role')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('title', 'Title')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('manager', 'Manager')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('status', 'Status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                {renderColumnHeader('lastLogin', 'Last Login')}
              </th>
              <th className="relative px-6 py-3 bg-gray-50">
                <span className="sr-only">Actions</span>
              </th>
                  </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.users?.map((tableUser: User) => (
                <tr key={tableUser.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {renderEditableField(tableUser, 'name', `${tableUser.firstName} ${tableUser.lastName}`)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tableUser.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex flex-wrap gap-1">
                      {tableUser.roles && tableUser.roles.length > 0 ? (
                        tableUser.roles.map((role, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              role === tableUser.role.name ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}
                            title={role === tableUser.role.name ? 'Primary Role' : ''}
                          >
                            {role}
                            {role === tableUser.role.name && <span className="ml-1 text-blue-600">★</span>}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {tableUser.role.name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tableUser.title || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tableUser.Manager ? (
                      <Link
                        to={`/users/${tableUser.Manager.id}`}
                        state={{ usersListSearch: location.search }}
                        className="text-indigo-600 hover:text-indigo-900 hover:underline"
                      >
                        {tableUser.Manager.firstName} {tableUser.Manager.lastName}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      tableUser.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {tableUser.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tableUser.lastLogin ? (
                      <div className="flex flex-col">
                        <span>{formatDate(tableUser.lastLogin)}</span>
                        <span className="text-xs">{formatTime(tableUser.lastLogin)}</span>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Link
                        to={`/users/${tableUser.id}`}
                        state={{ usersListSearch: location.search }}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="View User"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      {canDeleteUsers && (tableUser.role as any)?.name !== 'super_admin' && (
                        <button
                          onClick={() => handleDeleteUser(tableUser.id, `${tableUser.firstName} ${tableUser.lastName}`)}
                          className={`${
                            tableUser.id === user?.id 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title={
                            tableUser.id === user?.id 
                              ? "Cannot delete your own account" 
                              : "Delete user"
                          }
                          disabled={deleteUserMutation.isPending || tableUser.id === user?.id}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {(!data?.users || data.users.length === 0) && (
            <div className="text-center py-12">
              <p className="text-gray-500">No users found</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination Controls - Always visible */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
        {data?.total > 0 ? (
          <>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = previousSize >= data.total && size > data.total;
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data.total)} to {Math.min(page * pageSize, data.total)} of {data.total} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil(data.total / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({
                  page: String(Math.ceil(data.total / pageSize)),
                })
              }
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
          </>
        ) : (
          <div className="text-sm text-gray-500">No entries</div>
        )}
      </div>

      {/* Delete User Modal */}
      {userToDelete && (
        <UserDeleteModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          userId={userToDelete.id}
          userName={userToDelete.name}
        />
      )}
    </div>
  );
};

export default UserList;